// Original STORE OS illustrations: the home menu pictures, a phone drawn in the
// device's own color, and stand-in pictures for products that don't have a photo yet.
import { useId, type ReactElement, type ReactNode } from 'react';

const BLUE = '#0057FF';
const CYAN = '#00C8E8';
const NAVY = '#07152D';
const SKY = '#E6F0FF';
const LINE = '#C9D6EA';
const GREEN = '#12B76A';
const AMBER = '#F5A524';

function Frame({ children, label, size = 72 }: { children: ReactNode; label?: string; size?: number }) {
  return (
    <svg viewBox="0 0 96 96" width={size} height={size} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} className="art">
      {children}
    </svg>
  );
}

/** A modern phone, front view, with a screen gradient. */
function PhoneFront({ x, y, w, h, body = NAVY, screenA = BLUE, screenB = CYAN, id }: { x: number; y: number; w: number; h: number; body?: string; screenA?: string; screenB?: string; id: string }) {
  const r = w * 0.18;
  return (
    <g>
      <defs>
        <linearGradient id={`${id}-scr`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={screenA} />
          <stop offset="1" stopColor={screenB} />
        </linearGradient>
      </defs>
      <rect x={x} y={y} width={w} height={h} rx={r} fill={body} />
      <rect x={x + w * 0.08} y={y + w * 0.08} width={w * 0.84} height={h - w * 0.16} rx={r * 0.75} fill={`url(#${id}-scr)`} />
      <rect x={x + w * 0.36} y={y + w * 0.14} width={w * 0.28} height={w * 0.08} rx={w * 0.04} fill={body} />
      <path d={`M${x + w * 0.2} ${y + h * 0.3} q ${w * 0.3} -${h * 0.08} ${w * 0.6} 0`} stroke="#fff" strokeOpacity="0.35" strokeWidth={w * 0.05} fill="none" strokeLinecap="round" />
    </g>
  );
}

export function SaleArt({ size }: { size?: number }) {
  const id = useId();
  return (
    <Frame size={size}>
      <defs>
        <linearGradient id={`${id}-bag`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={BLUE} /><stop offset="1" stopColor="#0040C2" /></linearGradient>
      </defs>
      <path d="M30 36c0-10 8-18 18-18s18 8 18 18" stroke={NAVY} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M18 34h60l-5 46a6 6 0 0 1-6 5H29a6 6 0 0 1-6-5z" fill={`url(#${id}-bag)`} />
      <path d="M18 34h60l-1 8H19z" fill="#fff" opacity="0.18" />
      <g transform="rotate(-12 62 54)">
        <rect x="52" y="40" width="22" height="16" rx="3" fill={AMBER} />
        <circle cx="57" cy="48" r="2.2" fill="#fff" />
        <path d="M61 48h9" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      </g>
      <path d="M34 60l8 8 16-16" stroke="#fff" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function RepairArt({ size }: { size?: number }) {
  const id = useId();
  return (
    <Frame size={size}>
      <PhoneFront id={id} x={22} y={12} w={36} h={70} />
      <path d="M30 34l8 6-5 7 9 6-4 8" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      <g transform="rotate(40 66 58)">
        <rect x="62" y="22" width="8" height="40" rx="2" fill="#AEB9C9" />
        <rect x="59" y="56" width="14" height="26" rx="5" fill={AMBER} />
        <rect x="63" y="60" width="2.5" height="18" rx="1.2" fill="#fff" opacity="0.6" />
        <path d="M64 22l2-6 2 6z" fill="#AEB9C9" />
      </g>
    </Frame>
  );
}

export function BuyArt({ size }: { size?: number }) {
  const id = useId();
  return (
    <Frame size={size}>
      <PhoneFront id={id} x={14} y={14} w={34} h={66} />
      <g transform="rotate(-8 64 54)">
        <rect x="40" y="38" width="46" height="28" rx="5" fill={GREEN} />
        <rect x="44" y="42" width="38" height="20" rx="3" fill="none" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.5" />
        <circle cx="63" cy="52" r="7" fill="#fff" opacity="0.9" />
        <text x="63" y="56.5" textAnchor="middle" fontSize="11" fontWeight="700" fill={GREEN} fontFamily="DejaVu Sans, Arial, sans-serif">$</text>
      </g>
      <circle cx="74" cy="76" r="8" fill={AMBER} />
      <circle cx="74" cy="76" r="5" fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="1.5" />
    </Frame>
  );
}

export function TradeArt({ size }: { size?: number }) {
  const id = useId();
  return (
    <Frame size={size}>
      <PhoneFront id={`${id}a`} x={10} y={22} w={28} h={54} body="#5B6B82" screenA="#9AA8BC" screenB="#C9D3E1" />
      <PhoneFront id={`${id}b`} x={58} y={20} w={28} h={56} />
      <path d="M40 34c6-8 14-8 18 0" stroke={CYAN} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M55 27l4 8-9 1" stroke={CYAN} strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M56 64c-6 8-14 8-18 0" stroke={BLUE} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M41 71l-4-8 9-1" stroke={BLUE} strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function ActivationArt({ size }: { size?: number }) {
  return (
    <Frame size={size}>
      <path d="M24 14h32l16 16v48a6 6 0 0 1-6 6H24a6 6 0 0 1-6-6V20a6 6 0 0 1 6-6z" fill={BLUE} />
      <path d="M56 14v12a4 4 0 0 0 4 4h12z" fill="#003FB8" />
      <rect x="30" y="42" width="30" height="30" rx="5" fill={AMBER} />
      <path d="M30 52h30M30 62h30M45 42v30" stroke="#C97F00" strokeWidth="2" />
      <path d="M70 10a14 14 0 0 1 10 10M70 2a22 22 0 0 1 18 18" stroke={CYAN} strokeWidth="4" fill="none" strokeLinecap="round" />
    </Frame>
  );
}

export function BillArt({ size }: { size?: number }) {
  return (
    <Frame size={size}>
      <path d="M22 10h44a4 4 0 0 1 4 4v72l-8-5-8 5-8-5-8 5-8-5-8 5V14a4 4 0 0 1 4-4z" fill="#fff" stroke={LINE} strokeWidth="2" />
      <rect x="28" y="20" width="24" height="5" rx="2.5" fill={NAVY} />
      <path d="M28 34h32M28 42h32M28 50h20" stroke={LINE} strokeWidth="4" strokeLinecap="round" />
      <circle cx="68" cy="64" r="16" fill={GREEN} />
      <path d="M60 64l6 6 10-11" stroke="#fff" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </Frame>
  );
}

export function UnlockArt({ size }: { size?: number }) {
  const id = useId();
  return (
    <Frame size={size}>
      <PhoneFront id={id} x={14} y={12} w={36} h={72} />
      <path d="M62 44v-9a10 10 0 0 1 19-4" stroke={NAVY} strokeWidth="6" fill="none" strokeLinecap="round" />
      <rect x="52" y="44" width="34" height="28" rx="6" fill={AMBER} />
      <circle cx="69" cy="56" r="4" fill={NAVY} />
      <rect x="67.5" y="57" width="3" height="8" rx="1.5" fill={NAVY} />
    </Frame>
  );
}

export function OrderArt({ size }: { size?: number }) {
  return (
    <Frame size={size}>
      <path d="M48 12l32 14v40L48 82 16 66V26z" fill="#E8B676" />
      <path d="M48 12l32 14-32 14-32-14z" fill="#F3CD94" />
      <path d="M48 40v42" stroke="#C99555" strokeWidth="2" />
      <path d="M30 19l32 14v10l-8-3v-8L22 23z" fill="#fff" opacity="0.55" />
      <circle cx="72" cy="70" r="14" fill={BLUE} />
      <path d="M72 62a6 6 0 0 1 6 6c0 5-6 10-6 10s-6-5-6-10a6 6 0 0 1 6-6z" fill="#fff" />
      <circle cx="72" cy="68" r="2.2" fill={BLUE} />
    </Frame>
  );
}

export const TASK_ART: Record<string, (p: { size?: number }) => ReactElement> = {
  sale: SaleArt,
  repair: RepairArt,
  buy: BuyArt,
  trade: TradeArt,
  activation: ActivationArt,
  billpay: BillArt,
  unlock: UnlockArt,
  order: OrderArt,
};

// ---------------------------------------------------------------- phones in their colors

const COLOR_HEX: [RegExp, string][] = [
  [/desert/i, '#BFA48F'], [/natural/i, '#A8A49C'], [/white tit/i, '#E6E3DD'], [/black tit/i, '#3A3A3C'], [/blue tit/i, '#3F4A5A'],
  [/silverblue|silver blue|icy/i, '#A7B7C9'], [/cosmic orange|orange/i, '#E8762D'], [/deep blue|navy|ultramarine/i, '#2F4A8A'],
  [/midnight|obsidian|onyx|phantom black|space (gray|grey|black)|graphite|black/i, '#2B2B2E'], [/starlight|cream|ivory/i, '#EFE8DC'],
  [/silver|platinum/i, '#D9DADC'], [/white|snow|porcelain/i, '#F2F2F2'], [/teal|mint|sage/i, '#5AA6A3'], [/pink|rose/i, '#F2C4CF'],
  [/purple|lavender|violet|lilac/i, '#8A78B8'], [/(product)?red|crimson/i, '#C8102E'], [/yellow|lemon/i, '#F4D35E'], [/gold|champagne/i, '#E6C7A1'],
  [/green|alpine/i, '#7FA88A'], [/blue|sierra|pacific|sky/i, '#5B86C5'], [/gray|grey|titanium|charcoal|hazel/i, '#8A8D93'],
];

export function colorHex(name: string | null | undefined): string {
  if (!name) return '#3A3A3C';
  for (const [re, hex] of COLOR_HEX) if (re.test(name)) return hex;
  return '#6B7280';
}

function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v + amt)));
  const r = c((n >> 16) & 255), g = c((n >> 8) & 255), b = c(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/** A phone (or tablet/console) drawn from the back in the device's color, with a camera like its family. */
export function DeviceArt({ brand, model, color, category, size = 72 }: { brand?: string | null; model?: string | null; color?: string | null; category?: string | null; size?: number }) {
  const id = useId();
  const body = colorHex(color);
  const edge = shade(body, -28);
  const light = shade(body, 30);
  const m = `${brand ?? ''} ${model ?? ''}`.toLowerCase();
  const tablet = /ipad|tab|tablet/.test(m) || category === 'ipads' || category === 'tablets';
  const laptop = /macbook|laptop/.test(m) || category === 'macbooks';
  const console = category === 'consoles' || /playstation|xbox|switch|console/.test(m);
  const desktop = category === 'desktop' || /^desktop\b/.test(m);
  const car = category === 'car' || /^car part\b/.test(m);
  const label = [brand, model, color].filter(Boolean).join(' ');
  if (desktop) {
    return (
      <Frame size={size} label={label}>
        <rect x="12" y="16" width="72" height="48" rx="5" fill={edge} />
        <rect x="16" y="20" width="64" height="40" rx="2" fill={`url(#${id}-g)`} />
        <defs><linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={BLUE} /><stop offset="1" stopColor={CYAN} /></linearGradient></defs>
        <path d="M40 64h16l3 12H37z" fill={body} />
        <rect x="30" y="76" width="36" height="5" rx="2.5" fill={edge} />
      </Frame>
    );
  }
  if (car) {
    return (
      <Frame size={size} label={label}>
        <path d="M14 58l6-16c2-5 6-8 11-8h34c5 0 9 3 11 8l6 16v12a4 4 0 0 1-4 4H18a4 4 0 0 1-4-4z" fill={NAVY} />
        <path d="M26 44c1-3 3-5 6-5h32c3 0 5 2 6 5l3 9H23z" fill={CYAN} opacity="0.85" />
        <circle cx="28" cy="74" r="8" fill="#fff" stroke={NAVY} strokeWidth="4" />
        <circle cx="68" cy="74" r="8" fill="#fff" stroke={NAVY} strokeWidth="4" />
        <rect x="18" y="58" width="10" height="5" rx="2" fill={CYAN} />
        <rect x="68" y="58" width="10" height="5" rx="2" fill={CYAN} />
      </Frame>
    );
  }
  if (laptop) {
    return (
      <Frame size={size} label={label}>
        <rect x="16" y="22" width="64" height="42" rx="4" fill={edge} />
        <rect x="20" y="26" width="56" height="34" rx="2" fill={`url(#${id}-g)`} />
        <defs><linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={BLUE} /><stop offset="1" stopColor={CYAN} /></linearGradient></defs>
        <path d="M8 66h80l-4 8H12z" fill={body} />
        <rect x="40" y="66" width="16" height="3" rx="1.5" fill={edge} />
      </Frame>
    );
  }
  if (console) {
    return (
      <Frame size={size} label={label}>
        <path d="M30 10c8 0 12 4 12 10v56c0 6-4 10-12 10s-10-4-10-10V20c0-6 2-10 10-10z" fill={body} stroke={LINE} strokeWidth="2" />
        <rect x="42" y="16" width="14" height="64" rx="4" fill={NAVY} />
        <path d="M66 10c-8 0-10 4-10 10v56c0 6 2 10 10 10s10-4 10-10V20c0-6-2-10-10-10z" fill={body} stroke={LINE} strokeWidth="2" />
        <circle cx="49" cy="28" r="2.5" fill={CYAN} />
      </Frame>
    );
  }
  const w = tablet ? 56 : 40;
  const h = tablet ? 74 : 78;
  const x = (96 - w) / 2;
  const y = (96 - h) / 2;
  const samsung = /samsung|galaxy/.test(m);
  const pixel = /pixel|google/.test(m);
  const pro = /pro|ultra|max/.test(m);
  return (
    <Frame size={size} label={label}>
      <defs>
        <linearGradient id={`${id}-b`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={light} /><stop offset="1" stopColor={body} /></linearGradient>
      </defs>
      <rect x={x} y={y} width={w} height={h} rx={tablet ? 7 : 9} fill={edge} />
      <rect x={x + 1.5} y={y + 1.5} width={w - 3} height={h - 3} rx={tablet ? 6 : 8} fill={`url(#${id}-b)`} />
      {pixel ? (
        <g>
          <rect x={x + 1.5} y={y + 14} width={w - 3} height={11} rx={5} fill={shade(body, -45)} />
          <circle cx={x + 11} cy={y + 19.5} r={3} fill={NAVY} /><circle cx={x + 19} cy={y + 19.5} r={3} fill={NAVY} />
        </g>
      ) : samsung ? (
        <g>
          {[0, 1, 2].slice(0, pro ? 3 : 2).map((i) => <circle key={i} cx={x + 9} cy={y + 10 + i * 8} r={3.2} fill={NAVY} stroke={edge} strokeWidth="1.5" />)}
        </g>
      ) : (
        <g>
          <rect x={x + 4} y={y + 4} width={tablet ? 13 : 19} height={tablet ? 13 : 19} rx={5} fill={shade(body, -18)} stroke={edge} strokeWidth="1" />
          <circle cx={x + 8.8} cy={y + 8.8} r={tablet ? 3 : 3.6} fill={NAVY} />
          {!tablet ? <circle cx={x + 8.8} cy={y + 18} r={3.6} fill={NAVY} /> : null}
          {!tablet && pro ? <circle cx={x + 17.4} cy={y + 13.4} r={3.6} fill={NAVY} /> : null}
          {!tablet ? <circle cx={x + 17.6} cy={y + 7.4} r={1.4} fill="#F3F3F3" /> : null}
        </g>
      )}
      <path d={`M${x + w * 0.62} ${y + 8} l${w * 0.24} 0`} stroke="#fff" strokeOpacity="0.35" strokeWidth="2.5" strokeLinecap="round" />
    </Frame>
  );
}

// ---------------------------------------------------------------- stand-in product pictures

function Plate({ children, size, label }: { children: ReactNode; size?: number; label: string }) {
  return (
    <Frame size={size} label={label}>
      <rect x="4" y="4" width="88" height="88" rx="18" fill={SKY} />
      {children}
    </Frame>
  );
}

const PRODUCT_ART: Record<string, (size?: number) => ReactElement> = {
  cases: (s) => <Plate size={s} label="Phone case"><rect x="30" y="14" width="36" height="68" rx="10" fill={BLUE} /><rect x="36" y="20" width="24" height="56" rx="6" fill="#3D7BFF" /><rect x="34" y="18" width="14" height="14" rx="4" fill={NAVY} /></Plate>,
  screen_protectors: (s) => <Plate size={s} label="Screen protector"><rect x="28" y="14" width="40" height="68" rx="8" fill="#fff" stroke={LINE} strokeWidth="2" /><path d="M34 28l14-10M34 44l28-20M40 60l24-17" stroke={CYAN} strokeWidth="3" strokeLinecap="round" opacity="0.7" /></Plate>,
  chargers: (s) => <Plate size={s} label="Charger"><rect x="28" y="30" width="40" height="44" rx="9" fill="#fff" stroke={LINE} strokeWidth="2" /><rect x="38" y="16" width="5" height="16" rx="2" fill="#AEB9C9" /><rect x="53" y="16" width="5" height="16" rx="2" fill="#AEB9C9" /><rect x="42" y="58" width="12" height="5" rx="2.5" fill={NAVY} /></Plate>,
  cables: (s) => <Plate size={s} label="Cable"><path d="M24 70c0-16 48-8 48-26S40 22 40 38" stroke={NAVY} strokeWidth="5" fill="none" strokeLinecap="round" /><rect x="64" y="64" width="16" height="10" rx="3" fill="#AEB9C9" transform="rotate(-40 72 69)" /><rect x="32" y="22" width="12" height="18" rx="3" fill="#fff" stroke={LINE} strokeWidth="2" /></Plate>,
  power_banks: (s) => <Plate size={s} label="Power bank"><rect x="26" y="20" width="44" height="58" rx="10" fill={NAVY} /><path d="M50 32l-10 18h9l-3 14 12-20h-9z" fill={CYAN} /></Plate>,
  headphones: (s) => <Plate size={s} label="Earbuds"><rect x="24" y="44" width="48" height="30" rx="14" fill="#fff" stroke={LINE} strokeWidth="2" /><path d="M24 58h48" stroke={LINE} strokeWidth="2" /><path d="M34 22a8 8 0 0 1 8 8v12h-6V32" fill="#fff" stroke={NAVY} strokeWidth="3" /><path d="M62 22a8 8 0 0 0-8 8v12h6V32" fill="#fff" stroke={NAVY} strokeWidth="3" /></Plate>,
  speakers: (s) => <Plate size={s} label="Speaker"><rect x="22" y="26" width="52" height="46" rx="14" fill={NAVY} /><circle cx="48" cy="49" r="13" fill="#24365A" /><circle cx="48" cy="49" r="6" fill={CYAN} /></Plate>,
  adapters: (s) => <Plate size={s} label="Adapter"><rect x="40" y="16" width="16" height="30" rx="4" fill="#fff" stroke={LINE} strokeWidth="2" /><path d="M48 46v14" stroke={NAVY} strokeWidth="4" /><circle cx="48" cy="68" r="10" fill={NAVY} /><circle cx="48" cy="68" r="3.5" fill="#fff" /></Plate>,
  perfumes: (s) => <Plate size={s} label="Perfume"><rect x="42" y="14" width="12" height="12" rx="2" fill={AMBER} /><rect x="38" y="24" width="20" height="6" rx="2" fill="#C99555" /><path d="M28 34h40a6 6 0 0 1 6 6v32a8 8 0 0 1-8 8H30a8 8 0 0 1-8-8V40a6 6 0 0 1 6-6z" fill="#F7D9C4" stroke="#E8B676" strokeWidth="2" /><path d="M32 42v28" stroke="#fff" strokeWidth="4" strokeLinecap="round" opacity="0.7" /></Plate>,
  screens: (s) => <Plate size={s} label="Screen"><rect x="26" y="12" width="44" height="72" rx="8" fill={NAVY} /><rect x="30" y="16" width="36" height="64" rx="5" fill={BLUE} /><path d="M34 30l24-10" stroke="#fff" strokeOpacity="0.35" strokeWidth="3" strokeLinecap="round" /><path d="M62 84v6h8" stroke={AMBER} strokeWidth="3" fill="none" /></Plate>,
  batteries: (s) => <Plate size={s} label="Battery"><rect x="28" y="20" width="40" height="60" rx="6" fill={NAVY} /><rect x="34" y="28" width="28" height="44" rx="3" fill={GREEN} /><path d="M50 36l-8 14h7l-3 12 10-16h-7z" fill="#fff" /><rect x="42" y="14" width="12" height="8" rx="2" fill={AMBER} /></Plate>,
  charging_ports: (s) => <Plate size={s} label="Charging port"><path d="M20 58h56" stroke={AMBER} strokeWidth="10" strokeLinecap="round" /><rect x="38" y="40" width="20" height="12" rx="4" fill="#AEB9C9" /><rect x="42" y="44" width="12" height="4" rx="2" fill={NAVY} /><circle cx="26" cy="58" r="3" fill="#C97F00" /><circle cx="70" cy="58" r="3" fill="#C97F00" /></Plate>,
  cameras: (s) => <Plate size={s} label="Camera"><rect x="24" y="30" width="48" height="36" rx="8" fill={NAVY} /><circle cx="48" cy="48" r="12" fill="#24365A" /><circle cx="48" cy="48" r="6" fill={CYAN} /><circle cx="64" cy="38" r="2.5" fill={AMBER} /></Plate>,
  other: (s) => <Plate size={s} label="Item"><path d="M48 18l26 12v32L48 76 22 62V30z" fill="#E8B676" /><path d="M48 18l26 12-26 12-26-12z" fill="#F3CD94" /><path d="M48 42v34" stroke="#C99555" strokeWidth="2" /></Plate>,
};

const PARENT_ART: Record<string, string> = { accessories: 'cases', other_accessories: 'other', parts: 'screens', other_parts: 'other' };

/** The stand-in picture for a product without a photo, chosen by its category. */
export function ProductArt({ categoryId, size = 72 }: { categoryId?: string | null; size?: number }) {
  const key = categoryId && PRODUCT_ART[categoryId] ? categoryId : PARENT_ART[categoryId ?? ''] ?? 'other';
  return PRODUCT_ART[key](size);
}

/** A product's photo when it has one, otherwise the stand-in picture. */
export function ProductPicture({ url, categoryId, name, size = 72 }: { url?: string | null; categoryId?: string | null; name: string; size?: number }) {
  if (url) {
    return <img className="pic" src={url} alt={name} width={size} height={size} loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />;
  }
  return <ProductArt categoryId={categoryId} size={size} />;
}
